import type { Evaluation, Question, Skill } from '@/core/types';
import { catalog } from '@/data/catalog';
import { fold, tokenize } from '@/core/util/misc';
import { generateQuestion } from '@/engine/questions/generate';
import { analyseWriting, type WritingLang, type WritingReport } from '@/engine/writing';
import { analyseExplanation, type FeynmanResult } from '@/engine/feynman';
import { B, L, loc } from '@/i18n';
import { store } from '@/app/hooks';
import { aiReady, analysisModel, modelLabel, tutorModel } from './status';

/**
 * AI ARCHITECTURE — provider interfaces.
 * Studio ships with honest LOCAL implementations (content lookup, rule-based
 * analysis, graph planning). When AI is on (Settings → AI, the learner's own
 * key) Claude answers instead, with the local version as the fallback.
 * Screens only talk to these interfaces and always display which provider
 * produced an answer.
 */

export interface ProviderStatus {
  id: string;
  label: string;
  kind: 'local' | 'remote';
  connected: boolean;
  description: string;
}

export interface TutorReply {
  text: string;
  sources: string[];
  provider: ProviderStatus;
}

export interface TutorProvider {
  status: ProviderStatus;
  ask(question: string, ctx: { skillId?: string; currentQuestion?: Question }): Promise<TutorReply>;
}
export interface QuestionProvider {
  status: ProviderStatus;
  generate(skillId: string, difficulty: number): Promise<Question | null>;
}
export interface WritingProvider {
  status: ProviderStatus;
  analyse(text: string, lang: WritingLang, title?: string): Promise<WritingReport>;
}
export interface ExplanationProvider {
  status: ProviderStatus;
  evaluate(skill: Skill, text: string): Promise<FeynmanResult>;
}

const LOCAL = (id: string, label: [string, string], description: [string, string]): ProviderStatus =>
  loc({ id, label: B(...label), kind: 'local' as const, connected: true, description: B(...description) });

const localTutor: TutorProvider = {
  status: LOCAL('local-tutor', ['Local tutor (content lookup)', 'Lokal hjelper (innholdssøk)'], ['Searches Studio\'s own lesson content, definitions, misconceptions and worked solutions. No AI model is connected.', 'Søker i Studios eget innhold: forklaringer, begreper, misforståelser og løsninger. Ingen KI-modell er koblet til.']),
  async ask(question, ctx) {
    const skill = ctx.skillId ? catalog.skill(ctx.skillId) : undefined;
    const terms = tokenize(question);
    const passages: { text: string; source: string; score: number }[] = [];
    const consider = (text: string, source: string) => {
      const t = fold(text);
      const score = terms.reduce((s, w) => s + (t.includes(w) ? 1 : 0), 0);
      if (score > 0) passages.push({ text, source, score });
    };
    const skills = skill ? [skill, ...catalog.prerequisites(skill.id).map((id) => catalog.skill(id)!)] : catalog.skills;
    for (const s of skills) {
      consider(s.content.intro, `${s.name} · ${L('intro', 'intro')}`);
      s.content.explanation.forEach((p) => consider(p, `${s.name} · ${L('explanation', 'forklaring')}`));
      s.content.definitions.forEach((d) => consider(`${d.term}: ${d.meaning}`, `${s.name} · ${L('definition', 'begrep')}`));
      s.content.misconceptions.forEach((m) => consider(`${L('Common mistake', 'Vanlig feil')}: ${m.wrong}. ${m.why} ${m.fix}`, `${s.name} · ${L('misconception', 'misforståelse')}`));
    }
    if (ctx.currentQuestion && /hint|stuck|help|hjelp|how/i.test(question)) {
      const h = ctx.currentQuestion.hints[0];
      if (h) passages.push({ text: `${L('Hint for this task', 'Hint til oppgaven')}: ${h}`, source: L('Current question', 'Denne oppgaven'), score: 99 });
    }
    passages.sort((a, b) => b.score - a.score);
    const top = passages.slice(0, 3);
    if (!top.length) {
      return { text: L('I could not find that in Studio\'s content for this skill. Try rephrasing with a key term (e.g. "denominator", "slope", "ethos").', 'Fant ikke det i Studios innhold for denne ferdigheten. Prøv med et nøkkelord (f.eks. «nevner», «stigningstall», «etos»).'), sources: [], provider: this.status };
    }
    return { text: top.map((p) => p.text).join('\n\n'), sources: top.map((p) => p.source), provider: this.status };
  },
};

const localQuestions: QuestionProvider = {
  status: LOCAL('local-generators', ['Parameterised generators', 'Oppgavegeneratorer'], ['Questions are produced by Studio\'s validated, parameterised generators and authored banks.', 'Oppgavene lages av Studios testede generatorer og oppgavebanker.']),
  async generate(skillId, difficulty) {
    return generateQuestion({ skillId, difficulty, mode: 'practice' });
  },
};

const localWriting: WritingProvider = {
  status: LOCAL('local-writing', ['Rule-based writing analysis', 'Regelbasert tekstanalyse'], ['Hand-written pattern rules for structure, clarity, vocabulary, argumentation and common errors. Not AI.', 'Håndskrevne mønsterregler for struktur, klarhet, ordforråd, argumentasjon og vanlige feil. Ikke KI.']),
  async analyse(text, lang) {
    return analyseWriting(text, lang);
  },
};

const localExplain: ExplanationProvider = {
  status: LOCAL('local-feynman', ['Key-idea rubric', 'Nøkkelidé-sjekk'], ['Compares your explanation with the skill\'s key ideas and known misconceptions. Not AI.', 'Sammenligner forklaringen din med ferdighetens nøkkelideer og kjente misforståelser. Ikke KI.']),
  async evaluate(skill, text) {
    return analyseExplanation(skill, text);
  },
};

/* ------------------------------------------------------------------ */
/* Claude (the learner's own key) with the local version as fallback    */
/* ------------------------------------------------------------------ */

const REMOTE = (id: string, what: [string, string]): ProviderStatus => {
  const model = id === 'tutor' ? tutorModel() : analysisModel();
  return { id: `claude-${id}`, label: `Claude · ${modelLabel(model)}`, kind: 'remote', connected: true, description: L(...what) };
};

/** Notified when an AI request failed and the local version answered instead. */
type FallbackFn = (error: Error) => void;
const fallbackListeners = new Set<FallbackFn>();
export function onAiFallback(fn: FallbackFn) {
  fallbackListeners.add(fn);
  return () => { fallbackListeners.delete(fn); };
}

async function withFallback<T>(remote: () => Promise<T>, local: () => Promise<T>): Promise<T> {
  if (!aiReady()) return local();
  try {
    return await remote();
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    fallbackListeners.forEach((fn) => fn(e as Error));
    return local();
  }
}

const remote = () => import('./remote');

const smartTutor: TutorProvider = {
  get status() { return aiReady() ? REMOTE('tutor', ['Answers your question about this skill without giving away the task\'s answer.', 'Svarer på spørsmålet ditt om ferdigheten uten å avsløre svaret på oppgaven.']) : localTutor.status; },
  ask(question, ctx) {
    return withFallback(
      async () => ({ ...(await (await remote()).aiTutor(store.getState(), question, ctx)), provider: smartTutor.status }),
      () => localTutor.ask(question, ctx),
    );
  },
};

const smartWriting: WritingProvider = {
  get status() { return aiReady() ? REMOTE('writing', ['Claude reads your text like a teacher: scores, strengths and concrete tips with the principle behind each one.', 'Claude leser teksten som en lærer: poeng, styrker og konkrete tips med prinsippet bak hvert av dem.']) : localWriting.status; },
  analyse(text, lang, title = '') {
    return withFallback(
      async () => (await remote()).aiWriting(store.getState(), text, lang, title),
      async () => ({ ...(await localWriting.analyse(text, lang)), source: 'rules' as const }),
    );
  },
};

const smartExplain: ExplanationProvider = {
  get status() { return aiReady() ? REMOTE('feynman', ['Claude checks your explanation against the key ideas, finds mix-ups and suggests the next step.', 'Claude sjekker forklaringen mot nøkkelideene, finner misforståelser og foreslår neste steg.']) : localExplain.status; },
  evaluate(skill, text) {
    return withFallback(
      async () => (await remote()).aiFeynman(store.getState(), skill, text),
      async () => ({ ...(await localExplain.evaluate(skill, text)), source: 'local' as const }),
    );
  },
};

/**
 * Grades an open written answer with Claude when AI is on. Resolves to
 * `undefined` when AI is off or fails, so the caller uses the local rubric.
 */
export async function gradeWrittenAnswer(q: Question, answer: string): Promise<Evaluation | undefined> {
  if (!aiReady() || q.answer.type !== 'rubric' || !answer.trim()) return undefined;
  try {
    return await (await remote()).aiGrade(store.getState(), q, answer);
  } catch (e) {
    fallbackListeners.forEach((fn) => fn(e as Error));
    return undefined;
  }
}

export const providers = {
  tutor: smartTutor,
  questions: localQuestions as QuestionProvider,
  writing: smartWriting,
  explanations: smartExplain,
};

/** Future backend hook: swap a provider without touching UI code. */
export function registerProvider<K extends keyof typeof providers>(key: K, impl: (typeof providers)[K]) {
  providers[key] = impl;
}

export function providerStatuses(): ProviderStatus[] {
  return Object.values(providers).map((p) => p.status);
}
